/**
 * Trust Center — public consolidation of every security artefact a
 * prospect's procurement team will ask for. Cuts a typical
 * security-questionnaire from weeks to one URL.
 *
 * Sections (top → bottom):
 *   1. Hero + live Audit-Integrity badge
 *   2. Compliance frameworks grid (mirrors /security)
 *   3. Document library — markdown docs with sha256 + download
 *   4. SBOM download (CycloneDX 1.6)
 *   5. Responsible disclosure (PGP key fingerprint placeholder + security.txt)
 *   6. Vendor & sub-processor disclosure
 *   7. Contact card
 *
 * No auth required. Aggressive client-side caching of the docs list
 * (loaded once on mount) — refresh via browser hard-reload.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ExternalLink,
  Key, Mail, Globe, ArrowRight, Building2, FileText,
} from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import PublicNavbar from '@/components/PublicNavbar';
import { BrochureLangMenu } from '@/components/BrochureLangMenu';
import AuditIntegrityBadge from '@/components/AuditIntegrityBadge';
import AuditIntegrityPill from '@/components/AuditIntegrityPill';
import NetworkCanvas from '@/components/NetworkCanvas';
import UptimeWidget from '@/components/UptimeWidget';
import { OOHBriefSection } from '@/components/OOH';
import { DocumentLibrarySection, VendorTableSection } from './trust/sections';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const COMPLIANCE = [
  { id: 'owasp',     code: 'OWASP',  title: 'OWASP ASVS + Top 10', body: 'Full coverage matrix. Strict CSP, HSTS, anti-CSRF, brute-force lockouts, per-device JWT, hash-chained audit log.' },
  { id: 'iso27001',  code: 'ISO/IEC',title: 'ISO 27001 (Annex A)', body: 'Clause-by-clause: A.5 policies, A.9 access control, A.10 cryptography, A.12 logging, A.13 network, A.15 supplier risk, A.16 IR.' },
  { id: 'soc2',      code: 'SOC 2',  title: 'SOC 2 Type II readiness', body: 'TSC CC1, CC6, CC7, CC8, CC9 addressed. Audit trail, change mgmt, vendor risk register, IR playbook in place.' },
  { id: 'nist',      code: 'NIST',   title: 'NIST 800-53 (Moderate)', body: 'AC, AU, IA, SC, SI families. AU-9 tamper-evident logs verifiable live above.' },
  { id: 'gdpr',      code: 'GDPR',   title: 'GDPR Art. 15 / 17 / 20 / 32 / 33', body: 'Right to access + export + erasure available from your Profile. 72h breach notification process documented.' },
  { id: 'nist63b',   code: 'NIST',   title: 'NIST 800-63B passwords', body: '12+ char minimum, blocklist against known breaches, no forced rotation, MFA-like passwordless magic-links.' },
];

export default function TrustCenter() {
  const { effective } = useTheme();
  const isDark = effective === 'dark';
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [packManifest, setPackManifest] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/trust/docs`)
      .then((r) => r.json())
      .then((j) => { if (!cancelled) setDocs(j.docs || []); })
      .catch((err) => { console.warn('TrustCenter: docs fetch failed', err); })
      .finally(() => { if (!cancelled) setDocsLoading(false); });
    // Fetch the bundle manifest so the CTA can show its integrity hash inline.
    fetch(`${API_BASE}/trust/pack/manifest`)
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => { if (!cancelled && m) setPackManifest(m); })
      .catch((err) => { console.warn('TrustCenter: manifest fetch failed', err); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      className={`relative min-h-screen ${isDark ? 'text-white' : 'text-slate-900'}`}
      data-testid="trust-center-page"
      style={{
        // v266.6 — unified with Login/Home lavender palette. Boosted
        // violet stops and pinned base color to `var(--ve-bg)` so the
        // whole marketing surface reads as one violet-tinted paper.
        background: isDark
          ? 'radial-gradient(1300px 700px at 85% -10%, rgba(174,82,234,0.18), transparent 60%),'
          + ' radial-gradient(1000px 700px at -10% 110%, rgba(94,31,139,0.16), transparent 60%),'
          + ' radial-gradient(900px 900px at 50% 50%, rgba(121,50,174,0.08), transparent 70%),'
          + ' #000000'
          : 'radial-gradient(1300px 700px at 85% -10%, rgba(174,82,234,0.16), transparent 60%),'
          + ' radial-gradient(1000px 700px at -10% 110%, rgba(94,31,139,0.14), transparent 60%),'
          + ' radial-gradient(900px 900px at 50% 50%, rgba(121,50,174,0.10), transparent 70%),'
          + ' var(--ve-bg)',
      }}
    >
      {/* v163 — Same shared <NetworkCanvas /> the home page uses, so
          the live particle network sits behind every public surface
          (home, login, trust, pricing) for one unified visual identity. */}
      <NetworkCanvas isDark={isDark} testId="trust-network-canvas" />

      {/* schema.org JSON-LD — helps Google surface this page for
          "vigilant entities soc 2", "vigilant entities dpa", etc. */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Organization',
            'name': 'Vigilant Entities',
            'url': 'https://vigilantentities.com',
            'logo': 'https://vigilantentities.com/vigilant-mark.png',
            'sameAs': ['https://vigilantentities.com'],
            'contactPoint': {
              '@type': 'ContactPoint',
              'contactType': 'security',
              'email': 'security@vigilantentities.com',
              'availableLanguage': ['en'],
            },
          },
          {
            '@type': 'WebPage',
            'name': 'Vigilant Entities Trust Center',
            'description': 'Compliance evidence pack for procurement teams — SOC 2, ISO 27001, NIST, GDPR, OWASP. Live audit-log integrity verification, downloadable SECURITY/THREAT_MODEL/RBAC_MATRIX/INCIDENT_RESPONSE/BACKUP_RUNBOOK/VENDOR_RISK docs + CycloneDX SBOM.',
            'url': 'https://vigilantentities.com/trust',
            'isPartOf': { '@type': 'WebSite', 'name': 'Vigilant Entities', 'url': 'https://vigilantentities.com' },
            'about': [
              { '@type': 'Thing', 'name': 'SOC 2 Type II' },
              { '@type': 'Thing', 'name': 'ISO 27001' },
              { '@type': 'Thing', 'name': 'NIST 800-53' },
              { '@type': 'Thing', 'name': 'GDPR' },
              { '@type': 'Thing', 'name': 'OWASP ASVS' },
            ],
            'mainContentOfPage': {
              '@type': 'WebPageElement',
              'name': 'Compliance evidence pack',
            },
          },
        ],
      }) }} />
      <div className="relative z-10">
      <PublicNavbar />

      {/* HERO */}
      <section className="ve-container pt-16 md:pt-24 pb-10">
        <div className="max-w-4xl">
          <div className="flex items-center gap-2">
            <Building2 size={14} style={{ color: 'var(--ve-eyebrow)' }} />
            <span className="text-[10px] uppercase tracking-[0.32em] font-bold" style={{ color: 'var(--ve-eyebrow)' }}>
              [TRUST CENTER · ONE PAGE, EVERY ANSWER]
            </span>
          </div>
          <h1 className={`display text-4xl md:text-6xl mt-5 leading-[1.05] ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Built for your{' '}
            <span className="italic" style={{ color: 'var(--ve-violet-bright)' }}>auditor</span>.
          </h1>
          <p className={`text-lg mt-5 max-w-3xl leading-relaxed ${isDark ? 'text-white/70' : 'text-slate-700'}`}>
            Procurement questionnaires usually take weeks. Ours takes one URL.
            Download our evidence pack, verify the audit log live, and skip
            the back-and-forth.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <BrochureLangMenu
              testId="trust-download-brochure"
              label="Download Sales Brochure (PDF)"
              iconSize={12}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.22em] font-bold border transition-colors ${
                isDark
                  ? 'border-[var(--ve-violet-bright)]/40 text-[var(--ve-violet-bright)] bg-white/[0.04] hover:bg-white/[0.08]'
                  : 'border-[var(--ve-violet-bright)]/40 text-[var(--ve-violet-shadow)] bg-white/70 hover:bg-white'
              }`}
            />
            <a
              href={`${BACKEND_URL}/api/architecture/download.pdf`}
              data-testid="trust-download-architecture"
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.22em] font-bold transition-colors ${
                isDark
                  ? 'bg-[var(--ve-violet-bright)] text-black hover:brightness-110'
                  : 'bg-[var(--ve-violet-shadow)] text-white hover:brightness-110'
              }`}
              target="_blank"
              rel="noopener noreferrer"
              title="14-page technical architecture whitepaper — for CTOs, CISOs, DPOs, and procurement"
            >
              <FileText size={12} /> Technical Architecture Whitepaper
            </a>
          </div>
        </div>
      </section>

      {/* LIVE INTEGRITY BADGE */}
      <section className="ve-container pb-12">
        <AuditIntegrityBadge />
      </section>

      {/* 90-DAY UPTIME WIDGET */}
      <section className="ve-container pb-12" data-testid="trust-uptime-section">
        <UptimeWidget isDark={isDark} />
      </section>

      {/* OMNISCIENT ORBITAL HARMONY — patent-pending IP brief */}
      <section className="ve-container pb-16" data-testid="trust-ooh">
        <OOHBriefSection />
      </section>

      {/* COMPLIANCE GRID */}
      <section className="ve-container pb-16" data-testid="trust-compliance">
        <div className="max-w-3xl">
          <div className="ve-eyebrow" style={{ color: 'var(--ve-violet-bright)' }}>Frameworks</div>
          <h2 className={`display text-3xl md:text-4xl mt-2 leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Aligned with international standards.
          </h2>
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {COMPLIANCE.map((c) => (
            <div
              key={c.id}
              data-testid={`trust-compliance-card-${c.id}`}
              className={`rounded-lg border p-5 ${
                isDark
                  ? 'border-white/10 bg-white/[0.03]'
                  : 'border-slate-200 bg-white'
              }`}
            >
              <div className="text-[9px] uppercase tracking-[0.28em] font-bold mb-2" style={{ color: 'var(--ve-eyebrow)' }}>
                [{c.code}]
              </div>
              <div className="font-bold text-sm leading-tight">{c.title}</div>
              <p className={`mt-2 text-[12px] leading-relaxed ${isDark ? 'text-white/65' : 'text-slate-600'}`}>{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* DOCUMENT LIBRARY */}
      <DocumentLibrarySection
        apiBase={API_BASE}
        isDark={isDark}
        packManifest={packManifest}
        docs={docs}
        docsLoading={docsLoading}
      />

      {/* RESPONSIBLE DISCLOSURE */}
      <section className="ve-container pb-16" data-testid="trust-disclosure">
        <div className="max-w-3xl">
          <div className="ve-eyebrow" style={{ color: 'var(--ve-violet-bright)' }}>Responsible disclosure</div>
          <h2 className={`display text-3xl md:text-4xl mt-2 leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Found a bug? Tell us — encrypted.
          </h2>
        </div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
          <DisclosureCard
            isDark={isDark}
            icon={Mail}
            eyebrow="[CONTACT]"
            label="security@vigilantentities.com"
            href="mailto:security@vigilantentities.com"
            note="24h ack · 5-day triage SLA"
          />
          <DisclosureCard
            isDark={isDark}
            icon={Key}
            eyebrow="[ENCRYPTION]"
            label="PGP key fingerprint"
            href="/.well-known/security-pubkey.asc"
            note="Download from security.txt"
          />
          <DisclosureCard
            isDark={isDark}
            icon={Globe}
            eyebrow="[RFC 9116]"
            label="security.txt"
            href="/.well-known/security.txt"
            note="Canonical disclosure index"
          />
        </div>
      </section>

      {/* VENDOR LIST */}
      <VendorTableSection isDark={isDark} />

      {/* CTA */}
      <section className="ve-container pb-24">
        <div className={`rounded-lg border p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 ${
          isDark ? 'border-white/10 bg-white/[0.02]' : 'border-slate-200 bg-white'
        }`}>
          <div className="max-w-xl">
            <div className="ve-eyebrow" style={{ color: 'var(--ve-violet-bright)' }}>Still need more</div>
            <h3 className={`display text-2xl mt-2 leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Get the formal pack delivered.
            </h3>
            <p className={`mt-2 text-sm ${isDark ? 'text-white/65' : 'text-slate-600'}`}>
              SOC 2 letter + DPA + pen-test summary — sent on request. Email{' '}
              <a href="mailto:security@vigilantentities.com" className="underline">security@vigilantentities.com</a>
              {' '}with your procurement team&apos;s CISO copied.
            </p>
          </div>
          <Link
            to="/security"
            data-testid="trust-cta-security"
            className="ve-btn-primary shrink-0"
          >
            Live security page <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className={`border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
        <div className="ve-container py-8 flex flex-wrap items-center justify-between gap-3">
          <div className={`flex items-center gap-4 flex-wrap text-[10px] uppercase tracking-[0.22em] ${isDark ? 'text-white/45' : 'text-slate-500'}`}>
            <span>© 2026 Vigilant Entities</span>
            <AuditIntegrityPill />
          </div>
          <div className={`flex items-center gap-4 text-[11px] uppercase tracking-[0.18em] font-bold ${isDark ? 'text-white/65' : 'text-slate-600'}`}>
            <Link to="/security" className="hover:text-[var(--ve-violet-bright)]">Security</Link>
            <Link to="/pricing" className="hover:text-[var(--ve-violet-bright)]">Pricing</Link>
            <Link to="/" className="hover:text-[var(--ve-violet-bright)]">Home</Link>
          </div>
        </div>
      </footer>
      </div>
    </div>
  );
}

function DisclosureCard({ isDark, icon: Icon, eyebrow, label, href, note }) {
  return (
    <a
      href={href}
      target={href.startsWith('http') || href.startsWith('/.well-known') ? '_blank' : undefined}
      rel="noreferrer"
      className={`rounded-lg border p-5 transition-colors group ${
        isDark
          ? 'border-white/10 bg-white/[0.03] hover:border-[var(--ve-violet-bright)]/40'
          : 'border-slate-200 bg-white hover:border-[var(--ve-violet)]/30'
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className="h-9 w-9 rounded-md flex items-center justify-center border shrink-0"
          style={{
            backgroundColor: 'rgba(174,82,234,0.10)',
            color: 'var(--ve-violet-bright)',
            borderColor: 'rgba(174,82,234,0.30)',
          }}
        >
          <Icon size={16} strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[9px] uppercase tracking-[0.28em] font-bold" style={{ color: 'var(--ve-eyebrow)' }}>
            {eyebrow}
          </div>
          <div className="font-bold text-sm mt-1 truncate group-hover:underline">{label}</div>
          <div className={`text-[11px] mt-1 ${isDark ? 'text-white/55' : 'text-slate-500'}`}>{note}</div>
        </div>
        <ExternalLink size={12} className="opacity-50 mt-1" />
      </div>
    </a>
  );
}
